import type { ComponentType } from 'react';

import get from 'lodash/get';
import { action, autorun, observable } from 'mobx';

import { BALANCE_THRESHOLD_WARNING } from '../../config/constants';
import { postRequest } from '../../services/api';
import { ldClient, resolveFlag } from '../../services/launchDarkly';
import { socket } from '../../services/socket';
import { devError } from '../../utils/log';
import type { CoinType } from '../cointype/CoinTypeStore';
import type { FeatureFetchStatus, FeatureManagerDialogType, IFeatureManagerStore } from '../feature-manager/types';
import type { RootStore } from '../RootStore';

import type { BalanceThresholdWarningData, BalanceThresholdWarningEvent } from './types';

export class LowBalanceStore implements IFeatureManagerStore {
  constructor(private readonly rootStore: RootStore) {
    this.rootStore = rootStore;

    const ccGiveawayOnLowBalanceFF = resolveFlag('cc_giveaway_on_low_balance', true);
    this.setLowBalanceFlag(ccGiveawayOnLowBalanceFF);

    ldClient?.on('ready', () => {
      this.setLowBalanceFlag(resolveFlag('cc_giveaway_on_low_balance', true));
    });

    ldClient?.on('change', (changes) => {
      if ('cc_giveaway_on_low_balance' in changes) {
        this.setLowBalanceFlag(resolveFlag('cc_giveaway_on_low_balance', true));
      }
    });

    autorun(() => {
      if (this.rootStore.authStore.isAuthenticationFlowDone && this.rootStore.authStore.authorized) {
        socket.on(BALANCE_THRESHOLD_WARNING, this.socketBalanceThresholdListener);
      } else {
        socket.off(BALANCE_THRESHOLD_WARNING, this.socketBalanceThresholdListener);
      }
    });
  }

  @observable
  public lowBalanceFlag: boolean = false;

  @action
  public setLowBalanceFlag(flag: boolean) {
    this.lowBalanceFlag = flag;
  }

  public balanceThreshold: Record<CoinType, BalanceThresholdWarningData | null> = {
    GC: null,
    SC: null,
  };

  private setBalanceThreshold(coin: CoinType, value: BalanceThresholdWarningData | null) {
    this.balanceThreshold[coin] = value;
  }

  public reset() {
    this.setBalanceThreshold('GC', null);
    this.setBalanceThreshold('SC', null);
  }

  private get thresholdValue() {
    return Number(
      this.balanceThreshold[this.rootStore.coinTypeStore.coinType]?.threshold ??
        get(this.rootStore.configStore.configs, 'low_balance_cc_threshold', 0),
    );
  }

  public get thresholdPrize() {
    return Number(
      this.balanceThreshold[this.rootStore.coinTypeStore.coinType]?.prize ??
        get(this.rootStore.configStore.configs, 'low_balance_cc_reward', 0),
    );
  }

  private socketBalanceThresholdListener = (ev: BalanceThresholdWarningEvent) => {
    if (ev.data.prize) {
      this.setBalanceThreshold(ev.data.coin, ev.data);
    }
  };

  public async claimRefillBonus() {
    try {
      await postRequest('/v2/user/claim-refill-bonus');
      await this.rootStore.walletStore.fetchWallet();
      return true;
    } catch (error) {
      devError('WalletStore.claimRefillBonus', error);
      return false;
    }
  }

  public openOutOfCoinsDialog() {
    this.rootStore.portalStore.openPortal(this.OutOfCoinsComponent);
  }

  public openMiniStoreOnLowBalance(trigger: 'low_balance' | 'insufficient_balance') {
    this.rootStore.payRedeemStore.openMiniStore({
      eventAction: 'impression',
      triggeredBy: trigger,
    });
  }

  private async verifyBalance(coinType: CoinType, balance: number = 0) {
    try {
      await this.rootStore.walletStore.fetchWallet();
      const walletBalance = this.rootStore.walletStore.wallet[coinType === 'GC' ? 'gcCoin' : 'totalScCoin'];
      if (walletBalance > balance) {
        // do nothing if we missed the event
        return false;
      }
    } catch (error) {
      devError('LowBalanceStore.verifyBalance', error);
      return false;
    }

    return true;
  }

  public roundEndListener = async () => {
    const { coinType } = this.rootStore.coinTypeStore;
    if (resolveFlag('cc_giveaway_on_low_balance', true) && this.balanceThreshold[coinType]?.prize) {
      if (await this.verifyBalance(coinType, this.balanceThreshold[coinType]?.balance)) {
        this.openOutOfCoinsDialog();
        return;
      }
    }
  };

  public dialogTypes: FeatureManagerDialogType[] = ['FreeCoinsCC'];

  OutOfCoinsComponent: ComponentType | null = null;

  fetch(): FeatureFetchStatus {
    if (!this.lowBalanceFlag) {
      return 'OFF';
    }

    return 'READY';
  }

  public startDialogs() {
    if (!this.lowBalanceFlag) {
      return false;
    }

    // We give bonus only for GC
    if (this.rootStore.coinTypeStore.coinType !== 'GC') {
      return false;
    }

    if (!this.rootStore.authStore.authorized) {
      return false;
    }

    // Only if the balance is below the threshold
    if (this.rootStore.walletStore.wallet.gcCoin >= this.thresholdValue) {
      return false;
    }

    if (!this.OutOfCoinsComponent) {
      console.error('OutOfCoinsComponent is not defined');
      return false;
    }

    this.openOutOfCoinsDialog();
    return true;
  }
}
